import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { Button } from "./button";
import { Input } from "./input";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Select } from "./select";

interface Column<T> {
  key: string;
  title: string;
  dataIndex?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  width?: number | string;
  align?: "left" | "center" | "right";
  sortable?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  search?: {
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
  };
  filters?: {
    activeFilter: string;
    onFilterChange: (filter: string) => void;
    filterOptions: { key: string; label: string; count?: number }[];
  };
  onRowClick?: (record: T, index: number) => void;
  onEdit?: (record: T) => void;
  rowKey?: string | ((record: T) => string);
  className?: string;
  emptyText?: string;
  emptyIcon?: React.ReactNode;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  pagination,
  search,
  filters,
  onRowClick,
  onEdit,
  rowKey = "id",
  className = "",
  emptyText = "No data found",
  emptyIcon = null,
}: DataTableProps<T>) {
  const getRowKey = (record: T, index: number): string => {
    return typeof rowKey === "function"
      ? rowKey(record)
      : record[rowKey] || index.toString();
  };

  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.pageSize)
    : 1;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex flex-col lg:flex-row gap-4 w-full lg:w-auto">
          {/* Filters */}
          {filters && (
            <div className="flex flex-wrap gap-2">
              {filters.filterOptions.map((filter) => (
                <Button
                  key={filter.key}
                  variant={
                    filters.activeFilter === filter.key ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => filters.onFilterChange(filter.key)}
                  className="relative"
                >
                  {filter.label}
                  {filter.count !== undefined && (
                    <span className="ml-1 bg-white bg-opacity-20 rounded-full px-1.5 text-xs">
                      {filter.count}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          )}

          {/* Search */}
          {search && (
            <div className="relative w-full lg:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={search.placeholder || "Search..."}
                className="pl-10"
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  style={{ width: column.width }}
                  className={`font-semibold text-gray-700 bg-[#EBEBEB] ${
                    column.align === "center"
                      ? "text-center"
                      : column.align === "right"
                      ? "text-right"
                      : "text-left"
                  }`}
                >
                  {column.title}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Loading state
              Array.from({ length: pagination?.pageSize || 10 }).map(
                (_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      </TableCell>
                    ))}
                  </TableRow>
                )
              )
            ) : data?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-500">
                    {emptyIcon && (
                      <div className="text-gray-400">{emptyIcon}</div>
                    )}
                    <div className="font-semibold">{emptyText}</div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((record, index) => (
                <TableRow
                  key={getRowKey(record, index)}
                  className={`${
                    onRowClick ? "cursor-pointer hover:bg-gray-50" : ""
                  } transition-colors`}
                  onClick={() => onRowClick?.(record, index)}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={`text-sm ${
                        column.align === "center"
                          ? "text-center"
                          : column.align === "right"
                          ? "text-right"
                          : "text-left"
                      }`}
                    >
                      {column.render
                        ? column.render(
                            record[column.dataIndex || column.key],
                            record,
                            index
                          )
                        : record[column.dataIndex || column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {pagination && totalPages > 0 && data?.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4 bg-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Left: Page X of Y + Page Size Selector */}
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  Page {pagination.current} of {totalPages}
                </div>

                {/* Page Size Selector with Custom Select */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <Select
                    customClass={`h-9`}
                    value={pagination.pageSize.toString()}
                    onValueChange={(value) => {
                      pagination.onChange(1, Number(value));
                    }}
                    options={[
                      { value: "10", label: "10" },
                      { value: "20", label: "20" },
                      { value: "30", label: "30" },
                      { value: "50", label: "50" },
                      { value: "100", label: "100" },
                    ]}
                    className="w-20"
                    dropdownPosition="top"
                  />
                  <span className="text-sm text-gray-600">entries</span>
                </div>
              </div>

              {/* Right: Navigation + Jump */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    pagination.onChange(
                      pagination.current - 1,
                      pagination.pageSize
                    );
                  }}
                  disabled={pagination.current === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {/* Current Page Display */}
                <span className="px-3 text-sm font-medium">
                  {pagination.current}
                </span>

                {/* Next Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    pagination.onChange(
                      pagination.current + 1,
                      pagination.pageSize
                    );
                  }}
                  disabled={pagination.current >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>

                {/* Jump to Page */}
                <span className="text-sm text-gray-600 ml-4">Go to page:</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  className="w-16 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const target = Number(
                        (e.target as HTMLInputElement).value
                      );

                      if (target >= 1 && target <= totalPages) {
                        pagination.onChange(target, pagination.pageSize);
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
